import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { getMyLibrary } from '../api/booksService';
import { getStudentQuotasSummary, QuotaType } from '../api/drmQuotasService';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import type { BookDto } from '../types/book';

export default function StudentQuotasPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);

  // Fetch user's library
  const {
    data: books = [],
    isLoading: isLoadingBooks,
    error: booksError,
  } = useQuery({
    queryKey: ['my-library'],
    queryFn: getMyLibrary,
  });

  // Fetch quotas for selected book
  const {
    data: quotas,
    isLoading: isLoadingQuotas,
    error: quotasError,
  } = useQuery({
    queryKey: ['student-quotas', user?.sub, selectedBookId],
    queryFn: () => {
      if (!user?.sub || !selectedBookId) return null;
      return getStudentQuotasSummary(user.sub, selectedBookId);
    },
    enabled: !!user?.sub && !!selectedBookId,
  });

  // Auto-select first book if available
  if (books.length > 0 && !selectedBookId) {
    setSelectedBookId(books[0].id);
  }

  if (isLoadingBooks) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (booksError) {
    return (
      <ErrorMessage
        message={t('App.StudentQuotas.LoadError')}
        className="m-4"
      />
    );
  }

  if (books.length === 0) {
    return (
      <div className="card max-w-2xl mx-auto mt-8 text-center">
        <div className="text-neutral-400 mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <h2 className="heading-2 mb-2">{t('App.StudentQuotas.NoBooks')}</h2>
        <p className="text-neutral-600">
          {t('App.StudentQuotas.NoBooksMessage')}
        </p>
      </div>
    );
  }

  const selectedBook = books.find((b) => b.id === selectedBookId);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="heading-1 mb-6">{t('App.StudentQuotas.Title')}</h1>

      <div className="card mb-6">
        <p className="text-neutral-600 mb-4">
          {t('App.StudentQuotas.Intro')}
        </p>

        {/* Book Selector */}
        <div>
          <label htmlFor="book-select" className="label">
            {t('App.StudentQuotas.SelectBook')}
          </label>
          <select
            id="book-select"
            value={selectedBookId || ''}
            onChange={(e) => setSelectedBookId(e.target.value)}
            className="input"
          >
            <option value="">{t('App.StudentQuotas.SelectBookPlaceholder')}</option>
            {books.map((book: BookDto) => (
              <option key={book.id} value={book.id}>
                {book.title} by {book.author}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Quotas Display */}
      {selectedBookId && selectedBook && (
        <div className="card">
          <h2 className="heading-2 mb-4">{selectedBook.title}</h2>
          <p className="text-sm text-neutral-500 mb-6">by {selectedBook.author}</p>

          {quotasError && (
            <ErrorMessage
              message={t('App.StudentQuotas.QuotaLoadError')}
              className="mb-4"
            />
          )}

          {isLoadingQuotas ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : quotas ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Print Quota */}
              <QuotaCard
                title={t('App.StudentQuotas.PrintQuota')}
                icon={
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                    />
                  </svg>
                }
                remaining={quotas.print.remaining}
                unit={t('App.StudentQuotas.Pages')}
                color="blue"
              />

              {/* Copy Quota */}
              <QuotaCard
                title={t('App.StudentQuotas.CopyQuota')}
                icon={
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                }
                remaining={quotas.copy.remaining}
                unit={t('App.StudentQuotas.Operations')}
                color="green"
              />

              {/* Download Quota */}
              <QuotaCard
                title={t('App.StudentQuotas.DownloadQuota')}
                icon={
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                }
                remaining={quotas.download.remaining}
                unit={t('App.StudentQuotas.Downloads')}
                color="purple"
              />
            </div>
          ) : null}

          {/* Info Box */}
          <div className="mt-6 bg-primary-50 border border-primary-200 rounded-md p-4 text-sm text-primary-800">
            <p className="font-medium mb-2">{t('App.StudentQuotas.AboutTitle')}</p>
            <ul className="space-y-1 text-xs">
              <li>• {t('App.StudentQuotas.AboutReset')}</li>
              <li>• {t('App.StudentQuotas.AboutPrint')}</li>
              <li>• {t('App.StudentQuotas.AboutCopy')}</li>
              <li>• {t('App.StudentQuotas.AboutDownload')}</li>
              <li>• {t('App.StudentQuotas.AboutAccessibility')}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

interface QuotaCardProps {
  title: string;
  icon: React.ReactNode;
  remaining: number;
  unit: string;
  color: 'blue' | 'green' | 'purple';
}

function QuotaCard({ title, icon, remaining, unit, color }: QuotaCardProps) {
  const { t } = useTranslation();
  const colorClasses = {
    blue: 'text-primary-600 bg-primary-50 border-primary-200',
    green: 'text-success-600 bg-success-50 border-success-200',
    purple: 'text-secondary-600 bg-secondary-50 border-secondary-200',
  };

  const statusColor = remaining > 10 ? 'text-success-600' : remaining > 0 ? 'text-warning-600' : 'text-error-600';

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={colorClasses[color].split(' ')[0]}>{icon}</div>
        <h3 className="font-semibold text-neutral-900">{title}</h3>
      </div>
      <div className={`text-3xl font-bold ${statusColor} mb-1`}>{remaining}</div>
      <div className="text-sm text-neutral-600">{remaining} {unit}</div>
    </div>
  );
}
